import { Entity, PaymentMode } from '@/data/enums';
import { useRoute } from '@/data/hooks';
import { useAppSelector } from '@/data/reducers';
import { NS_FOOTER } from '@/i18n/namespaces';
import { formatCurrency } from '@/utils/currency';
import { useTranslation } from 'react-i18next';
import { IoChevronBack } from 'react-icons/io5';
import { RiCoupon3Fill } from 'react-icons/ri';
import { twMerge } from 'tailwind-merge';

type SummaryFooterProps = {
  amount: number;
  paymentMode: PaymentMode;
  onBack?: () => void;
  disabled?: boolean;
  savingsAmount?: number;
  /**
   * Optional override for the submit-button label key (footer namespace).
   * Defaults to `continueButton` (or `updateButton` when editing).
   * Used by the Declaration / Review page to render "Proceed to pay".
   */
  ctaLabelKey?: string;
};

export const SummaryFooter = ({
  amount,
  paymentMode,
  onBack,
  disabled,
  savingsAmount,
  ctaLabelKey,
}: SummaryFooterProps) => {
  const application = useAppSelector((state) => state.application);
  const { entity } = useRoute();
  const { t } = useTranslation(NS_FOOTER);

  return (
    <div className="fixed bottom-0 w-full border-t border-[#E1E1E1] bg-white p-4">
      <div className="container mx-auto flex justify-between md:gap-12">
        <div className="flex grow flex-col justify-between gap-1 md:flex-row md:items-center">
          <div className="flex flex-row items-center gap-6">
            <button className="hidden md:block" onClick={onBack} type="button">
              <IoChevronBack className="size-6" />
            </button>

            <span className="text-sm md:text-2xl md:font-bold">
              {t(
                entity === Entity.TAKAFUL
                  ? 'totalContribution'
                  : 'totalPremium',
              )}
            </span>
          </div>

          <span className="text-xl font-bold md:text-2xl">
            {t(paymentMode === PaymentMode.Monthly ? 'perMonth' : 'perYear', {
              amount: formatCurrency(amount),
            })}
          </span>

          {savingsAmount != null &&
            savingsAmount > 0 &&
            paymentMode === PaymentMode.Annually && (
              <span className="flex items-center gap-1 text-sm text-amber-600 md:hidden">
                <RiCoupon3Fill className="size-4" />
                {t('youSave', { amount: formatCurrency(savingsAmount) })}
              </span>
            )}
        </div>

        <button
          type="submit"
          className={twMerge(
            'rounded bg-[#FFBF00] px-6 py-4 font-bold md:w-[250px]',
            disabled && 'bg-[#F2F2F2] text-[#B8BABC]',
          )}
          disabled={disabled}
        >
          {t(
            ctaLabelKey ??
              (application.isEdit ? 'updateButton' : 'continueButton'),
          )}
        </button>
      </div>
    </div>
  );
};
